import type { Patient } from '@/models/patient';
import type { MergedRecord, Overrides, OverrideableField } from '../types';

// Fields where the override is a single scalar that maps 1:1 to a MergedRecord
// field. `name`, `notes`, MRN, and DOB are also overrideable but need special
// handling (split name into first/last, treat notes override as replacement
// rather than concatenation, etc.) — handled inline below.
const SIMPLE_OVERRIDES: OverrideableField[] = [
  'sex',
  'referringPhysician',
  'phoneNumber',
  'address',
  'ethnicity',
];

export const isBlank = (v: string | number | null | undefined): boolean => {
  if (v === null || v === undefined) return true;
  if (typeof v === 'string') return v.trim() === '';
  return false;
};

const str = (v: string | null | undefined): string => (v ?? '').trim();

const formatFullName = (first: string | null, last: string | null): string => {
  const f = str(first);
  const l = str(last);
  if (!f && !l) return '';
  return `${f} ${l}`.trim();
};

const formatReferring = (p: Patient): string => {
  const f = str(p.referringPhysicianFirstName);
  const l = str(p.referringPhysicianLastName);
  if (!f && !l) return '';
  return `${f} ${l}`.trim();
};

/**
 * Primary Rule for the initial merged value: primary if present, else
 * secondary, else blank. The user-provided override then wins on submit
 * whenever it's non-blank (override-wins-when-non-blank).
 *
 * Sex is exposed as the raw enum (`MALE`/`FEMALE`/`OTHER`/'') so the editable
 * Select can bind to it directly. The Select renders the human label.
 */
export function computeMergedRecord(
  primary: Patient,
  secondary: Patient,
  overrides: Overrides = {}
): MergedRecord {
  const pick = (pVal: string, sVal: string): string =>
    !isBlank(pVal) ? pVal : sVal;

  let firstName = pick(str(primary.firstName), str(secondary.firstName));
  let lastName = pick(str(primary.lastName), str(secondary.lastName));

  let sex: string = pick(str(primary.sex), str(secondary.sex));
  let referringPhysician = pick(
    formatReferring(primary),
    formatReferring(secondary)
  );
  let phoneNumber = pick(str(primary.phoneNumber), str(secondary.phoneNumber));
  let address = pick(str(primary.address), str(secondary.address));
  let ethnicity = pick(str(primary.ethnicity), str(secondary.ethnicity));

  const assignments: Partial<Record<OverrideableField, (v: string) => void>> = {
    sex: (v) => {
      sex = v;
    },
    referringPhysician: (v) => {
      referringPhysician = v;
    },
    phoneNumber: (v) => {
      phoneNumber = v;
    },
    address: (v) => {
      address = v;
    },
    ethnicity: (v) => {
      ethnicity = v;
    },
  };

  SIMPLE_OVERRIDES.forEach((key) => {
    if (overrides[key] === undefined) return;
    assignments[key]?.(str(overrides[key]));
  });

  // Notes: concatenate by default; override replaces the concatenation.
  const primaryNotes = str(primary.notes);
  const secondaryNotes = str(secondary.notes);
  let notes = '';
  if (primaryNotes && secondaryNotes) {
    notes = `${primaryNotes}\n\n${secondaryNotes}`;
  } else if (primaryNotes) {
    notes = primaryNotes;
  } else if (secondaryNotes) {
    notes = secondaryNotes;
  }
  if (overrides.notes !== undefined) {
    notes = str(overrides.notes);
  }

  let mrn = pick(
    str(primary.medicalRecordNumber),
    str(secondary.medicalRecordNumber)
  );
  if (overrides.medicalRecordNumber !== undefined) {
    mrn = str(overrides.medicalRecordNumber);
  }

  let dob = pick(str(primary.dateOfBirth), str(secondary.dateOfBirth));
  if (overrides.dateOfBirth !== undefined) {
    dob = str(overrides.dateOfBirth);
  }

  // Name override is a single "First Last" string — split on whitespace, first
  // token is firstName and the remainder is lastName (BE does the same split).
  if (overrides.name !== undefined) {
    const tokens = str(overrides.name).split(/\s+/).filter(Boolean);
    firstName = tokens[0] ?? '';
    lastName = tokens.slice(1).join(' ');
  }

  return {
    firstName: firstName || null,
    lastName: lastName || null,
    fullName: formatFullName(firstName, lastName),
    medicalRecordNumber: mrn,
    dateOfBirth: dob,
    sex,
    referringPhysician,
    phoneNumber,
    address,
    ethnicity,
    notes,
    studyCount: (primary.studyCount ?? 0) + (secondary.studyCount ?? 0),
  };
}
